import { z } from "zod";
import { ProfileFieldValueSchema } from "@/profile-fields/contract";
export const QuestionSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string(),
  kind: z.enum(["text", "number", "date", "select", "boolean"]),
  required: z.boolean(),
  options: z.array(z.string()),
  profileFieldId: z.string().nullable(),
});
export const RewardSchema = z.object({
  badgeId: z.string(),
  questionId: z.string().nullable(),
  equals: ProfileFieldValueSchema,
  days: z.number().int(),
});
export const FormDefinitionSchema = z.object({
  title: z.string(),
  description: z.string(),
  completionTitle: z.string(),
  completionMessage: z.string(),
  buttonLabel: z.string(),
  appearance: z.enum(["brand", "neutral"]),
  public: z.boolean(),
  attemptLimit: z.number().int(),
  trigger: z.enum(["manual", "onboarding"]),
  roles: z.array(z.enum(["mentorado", "mentor", "admin"])),
  badgeIds: z.array(z.string()),
  redirectPath: z.string().nullable(),
  questions: z.array(QuestionSchema),
  rewards: z.array(RewardSchema),
});
export const RespondentDefinitionSchema = FormDefinitionSchema.omit({
  roles: true,
  badgeIds: true,
  rewards: true,
  questions: true,
}).extend({
  // `section` só vem nos diagnósticos do onboarding.
  questions: z.array(QuestionSchema.extend({ section: z.string().optional() })),
});
export const FormRecordSchema = z.object({
  id: z.string(),
  slug: z.string(),
  active: z.boolean(),
  version: z.number().int(),
  definition: FormDefinitionSchema,
});
export const AnswerSchema = z.object({ questionId: z.string(), value: ProfileFieldValueSchema });
export const CompletionSchema = z.object({
  attemptId: z.string(),
  formId: z.string(),
  userId: z.string(),
  version: z.number().int(),
  completedAt: z.string(),
  title: z.string(),
  message: z.string(),
  redirectPath: z.string().nullable(),
  profileFieldsUpdated: z.number().int(),
  rewards: z.array(
    z.object({
      badgeId: z.string(),
      name: z.string(),
      expiresAt: z.string().nullable(),
      renewed: z.boolean(),
    }),
  ),
});
export const AttemptSchema = z.object({
  id: z.string(),
  formId: z.string(),
  version: z.number().int(),
  state: z.enum(["open", "completed", "abandoned"]),
  definition: RespondentDefinitionSchema,
  prefill: z.array(AnswerSchema),
  completion: CompletionSchema.nullable(),
});
export const AvailableFormSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  description: z.string(),
  trigger: z.enum(["manual", "onboarding"]),
  questionCount: z.number().int(),
  attemptLimit: z.number().int(),
  completedCount: z.number().int(),
});
export const OwnFormSchema = z.object({
  id: z.string(),
  slug: z.string(),
  version: z.number().int(),
  definition: RespondentDefinitionSchema,
  completedCount: z.number().int(),
  openAttempt: z.object({ id: z.string(), version: z.number().int() }).nullable(),
  lastCompletion: CompletionSchema.nullable(),
  attempt: AttemptSchema.nullable(),
});
export const ResponsesSchema = z.object({
  items: z.array(
    z.object({
      attemptId: z.string(),
      userId: z.string(),
      name: z.string(),
      email: z.string(),
      version: z.number().int(),
      completedAt: z.string(),
      answers: z.array(AnswerSchema),
      questions: z.array(QuestionSchema),
      completion: CompletionSchema.nullable(),
    }),
  ),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});
export const PublicFormSchema = z.object({
  slug: z.string(),
  title: z.string(),
  description: z.string(),
  appearance: z.enum(["brand", "neutral"]),
  identificationRequired: z.literal(true),
});
export type FormDefinition = z.infer<typeof FormDefinitionSchema>;
export type FormRecord = z.infer<typeof FormRecordSchema>;
export type FormInput = FormDefinition & { slug: string; active: boolean };
export type FormQuestion = z.infer<typeof QuestionSchema>;
export type FormAnswer = z.infer<typeof AnswerSchema>;
export type FormCompletion = z.infer<typeof CompletionSchema>;
export type FormAttempt = z.infer<typeof AttemptSchema>;
export type RespondentDefinition = z.infer<typeof RespondentDefinitionSchema>;
export const FORM_REDIRECTS = {
  "/profile": "Perfil",
  "/courses": "Cursos",
  "/badges": "Selos",
  "/showcase": "Vitrine",
  "/feed": "Feed",
  "/forms": "Formulários",
} as const;
export const emptyForm = (): FormInput => ({
  slug: "",
  active: false,
  title: "",
  description: "",
  completionTitle: "Respostas enviadas",
  completionMessage: "Obrigado por compartilhar essas informações.",
  buttonLabel: "Enviar respostas",
  appearance: "brand",
  public: false,
  attemptLimit: 1,
  trigger: "manual",
  roles: ["mentorado"],
  badgeIds: [],
  redirectPath: null,
  questions: [],
  rewards: [],
});
export const emptyQuestion = (): FormQuestion => ({
  id: crypto.randomUUID(),
  label: "",
  description: "",
  kind: "text",
  required: false,
  options: [],
  profileFieldId: null,
});
export function answerValue(
  question: FormQuestion,
  value: FormAnswer["value"],
): FormAnswer["value"] {
  return question.kind === "number" && typeof value === "string"
    ? value.trim()
      ? Number(value)
      : null
    : value;
}
